import { Expose, plainToInstance, Transform } from 'class-transformer';
import { Type } from '@angular/core';
import { createTypedEntities } from './entities-utils';

export type MediaTypes = 'images' | 'svgs' | 'videos';

export class Media {
  @Expose()
  type!: string;

  @Expose()
  id!: string;
}

export class EmbeddedVideoMedia extends Media {
  static type = 'media--embedded_video';

  @Transform(({ obj }) => {
    return obj?.attributes?.field_media_video_embed_field;
  })
  @Expose()
  embedUrl!: string;
}

export class FileMedia extends Media {
  static type = 'media--file';
}

export interface ImageDerivative {
  href: string;
  width: number;
  height: number;
  title: string;
}

export interface ImageDerivatives {
  [profileId: string]: ImageDerivative;
}

export class ImageMedia extends Media {
  static type = 'images';

  @Transform(({ obj }) => {
    const imageDerivatives: ImageDerivatives =
      obj?.relationships?.fileRef?.data?.meta?.imageDerivatives?.links;
    let imageFiles: ImageFile[] | undefined;
    if (imageDerivatives) {
      //creates an array ignoring webp profiles
      const nativeImageDerivativesArray: string[] = Object.keys(
        imageDerivatives
      ).filter((profileId) => profileId.indexOf('webp') === -1);
      //iterate in native image profiles and add webp url
      imageFiles = [];
      nativeImageDerivativesArray.forEach((profileId) => {
        const imageFile: ImageFile = plainToInstance(
          ImageFile,
          imageDerivatives[profileId],
          {
            excludeExtraneousValues: true,
            exposeUnsetFields: false,
          }
        );
        imageFile.profileId = profileId;
        imageFile.alt = obj?.relationships?.fileRef?.data?.meta?.alt;
        imageFile.cropped = profileId.indexOf('crop_') > -1;
        if (imageDerivatives[`${profileId}_webp`]) {
          imageFile.webpUrl = imageDerivatives[`${profileId}_webp`].href;
        }
        if (imageFiles) {
          imageFiles.push(imageFile);
        }
      });
    }

    return imageFiles;
  })
  @Expose()
  images: ImageFile[] | undefined;
}

export class ImageFile {
  profileId!: string;

  // manage files coming from Media as imageDerivatives and file coming from File entity (ie SVG Media)
  @Transform(({ obj }) => {
    return obj?.href || obj?.attributes?.uri?.url;
  })
  @Expose()
  url!: string;

  @Transform(({ obj }) => {
    return obj?.width;
  })
  @Expose()
  width!: number;

  @Transform(({ obj }) => {
    return obj?.height;
  })
  @Expose()
  height!: number;

  @Transform(({ obj }) => {
    return obj?.meta?.alt;
  })
  @Expose()
  alt!: number;

  @Transform(({ obj }) => {
    return obj?.title;
  })
  @Expose()
  title!: number;

  webpUrl!: string;

  cropped!: boolean;
}

export class SvgMedia extends Media {
  static type = 'svgs';

  @Transform(({ obj }) => {
    const rawFile = obj?.relationships?.fileRef?.data?.reference;
    let imageFile: ImageFile | undefined;
    if (rawFile) {
      imageFile = plainToInstance(ImageFile, rawFile, {
        excludeExtraneousValues: true,
        exposeUnsetFields: false,
      });
    }
    return imageFile;
  })
  @Expose()
  file: ImageFile | undefined;
}

export class VideoMedia extends Media {
  static type = 'videos';

  @Transform(({ obj }) => {
    return obj?.relationships?.fileRef?.data?.reference?.attributes?.uri?.url;
  })
  @Expose()
  url!: string;
}
